import { BarChart, ColumnChart, DonutChart, FlowDiagram, Infographic, RangeChart } from "@/components/infographic";
import { getPublishedMethods } from "@/lib/queries";
import { getMethodFacts } from "@/lib/methodfacts";
import { PAYMENT_TESTS, TEST_SUMMARY, methodTestStats } from "@/lib/testdata";

const ROUND = "eigen meetreeks Casino Betalingen, hercontrole november 2026";

function methodsWithTests() {
  return getPublishedMethods()
    .map((m) => ({ method: m, stats: methodTestStats(m.slug) }))
    .filter((x) => x.stats.tests > 0);
}

/**
 * Snelheidsranking van alle geteste betaalmethoden: mediane tijd tot het
 * saldo zichtbaar is in het casinoaccount.
 */
export function DepositSpeedInfographic({ highlightSlug }: { highlightSlug?: string }) {
  const items = methodsWithTests()
    .sort((a, b) => (a.stats.medianDepositSeconds ?? 0) - (b.stats.medianDepositSeconds ?? 0))
    .map(({ method, stats }) => ({
      label: method.name,
      value: stats.medianDepositSeconds ?? 0,
      valueLabel: `${stats.medianDepositSeconds} sec.`,
      hint: `${stats.tests} transacties · spreiding ${stats.fastestDepositSeconds}–${stats.slowestDepositSeconds} sec.`,
      highlight: method.slug === highlightSlug,
    }));

  return (
    <Infographic
      id="infographic-stortsnelheid"
      kicker="Onderzoek · stortsnelheid"
      title="Hoe snel staat je storting op de rekening?"
      description={`Mediane tijd tussen bevestiging van de betaling en zichtbaar saldo, gemeten met een stopwatch over ${TEST_SUMMARY.testsRun} echte transacties bij ${TEST_SUMMARY.casinosTested} aanbieders.`}
      source={ROUND}
    >
      <BarChart
        items={items}
        caption="Mediane stortingstijd per betaalmethode in seconden"
        unitLabel="Mediane stortingstijd (seconden)"
      />
    </Infographic>
  );
}

/** Uitbetaaltijden per methode, in uren. */
export function PayoutSpeedInfographic({ highlightSlug }: { highlightSlug?: string }) {
  const items = methodsWithTests()
    .filter((x) => x.stats.withdrawalSupportedInTests)
    .sort((a, b) => (a.stats.medianWithdrawalHours ?? 0) - (b.stats.medianWithdrawalHours ?? 0))
    .map(({ method, stats }) => ({
      label: method.name,
      value: stats.medianWithdrawalHours ?? 0,
      valueLabel: `${stats.medianWithdrawalHours} uur`,
      hint: `snelste ${stats.fastestWithdrawalHours} uur · langzaamste ${stats.slowestWithdrawalHours} uur`,
      highlight: method.slug === highlightSlug,
    }));

  if (items.length === 0) return null;

  return (
    <Infographic
      id="infographic-uitbetaling"
      kicker="Onderzoek · uitbetaling"
      title="Mediane uitbetaaltijd per betaalmethode"
      description="Gemeten vanaf het indienen van het opnameverzoek tot het moment dat het geld daadwerkelijk is bijgeschreven, inclusief eventuele handmatige controle."
      source={ROUND}
    >
      <BarChart
        items={items}
        caption="Mediane uitbetaaltijd per betaalmethode in uren"
        unitLabel="Mediane uitbetaaltijd (uren)"
      />
    </Infographic>
  );
}

/** Spreiding van gemeten stortingstijden per methode (min–mediaan–max). */
export function SpreadInfographic() {
  const items = methodsWithTests()
    .sort((a, b) => (a.stats.medianDepositSeconds ?? 0) - (b.stats.medianDepositSeconds ?? 0))
    .map(({ method, stats }) => ({
      label: method.name,
      min: stats.fastestDepositSeconds ?? 0,
      median: stats.medianDepositSeconds ?? 0,
      max: stats.slowestDepositSeconds ?? 0,
      unit: "sec.",
    }));

  return (
    <Infographic
      id="infographic-spreiding"
      kicker="Onderzoek · betrouwbaarheid"
      title="Spreiding: hoe voorspelbaar is elke methode?"
      description="Een korte balk betekent dat elke transactie ongeveer even snel is. Een lange balk betekent dat de snelheid sterk afhangt van het casino en het tijdstip. Het groene streepje is de mediaan."
      source={ROUND}
    >
      <RangeChart
        items={items}
        caption="Snelste, mediane en langzaamste gemeten stortingstijd per betaalmethode"
        unitLabel="seconden"
      />
    </Infographic>
  );
}

/** Verdeling van betaalmethoden over categorieën plus dekking per casino. */
export function CoverageInfographic() {
  const methods = getPublishedMethods();
  const categories = [...new Set(methods.map((m) => m.category))].map((cat) => ({
    label: cat,
    value: methods.filter((m) => m.category === cat).length,
    valueLabel: `${methods.filter((m) => m.category === cat).length} methoden`,
    hint: methods
      .filter((m) => m.category === cat)
      .map((m) => m.name)
      .join(", "),
  }));

  const withdrawalShare = Math.round(
    (methods.filter((m) => m.withdrawal_supported).length / Math.max(1, methods.length)) * 100,
  );

  return (
    <Infographic
      id="infographic-dekking"
      kicker="Onderzoek · marktdekking"
      title="Welke soorten betaalmethoden vind je bij Nederlandse casino's?"
      description={`Onze redactie volgt ${methods.length} betaalmethoden. Niet elke methode werkt twee kanten op: alleen een deel is ook geschikt om winst mee te laten uitbetalen.`}
      source={`redactionele methodendatabase Casino Betalingen · ${ROUND}`}
    >
      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-center">
        <BarChart
          items={categories}
          caption="Aantal gevolgde betaalmethoden per categorie"
          unitLabel="Aantal methoden"
        />
        <DonutChart
          percentage={withdrawalShare}
          label="Ook geschikt voor uitbetaling"
          sublabel={`${methods.filter((m) => m.withdrawal_supported).length} van de ${methods.length} gevolgde methoden`}
        />
      </div>
    </Infographic>
  );
}

/** Testdekking per casino: hoeveel transacties we per aanbieder deden. */
export function TestCoverageInfographic() {
  const byCasino = new Map<string, number>();
  for (const t of PAYMENT_TESTS) byCasino.set(t.casino, (byCasino.get(t.casino) ?? 0) + 1);
  const items = [...byCasino.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([casino, count]) => ({
      label: casino,
      value: count,
      valueLabel: `${count} transacties`,
      hint: `${new Set(PAYMENT_TESTS.filter((t) => t.casino === casino).map((t) => t.method)).size} verschillende betaalmethoden getest`,
    }));

  return (
    <Infographic
      id="infographic-testdekking"
      kicker="Onderzoek · testdekking"
      title="Hoeveel we per aanbieder daadwerkelijk hebben getest"
      description={`Elke aanbieder in de vergelijker is met eigen geld en eigen accounts getest. In totaal gaat het om ${TEST_SUMMARY.testsRun} transacties in de ronde van ${TEST_SUMMARY.lastRound}.`}
      source={ROUND}
    >
      <BarChart items={items} caption="Aantal geteste transacties per casino" unitLabel="Aantal transacties" />
    </Infographic>
  );
}

/** Histogram van gemeten stortingstijden plus KYC-donut voor één methode. */
export function MethodDistributionInfographic({
  methodSlug,
  methodName,
}: {
  methodSlug: string;
  methodName: string;
}) {
  const stats = methodTestStats(methodSlug);
  if (stats.tests === 0) return null;

  const buckets = [
    { label: "0–5 sec.", test: (s: number) => s <= 5 },
    { label: "6–10 sec.", test: (s: number) => s > 5 && s <= 10 },
    { label: "11–20 sec.", test: (s: number) => s > 10 && s <= 20 },
    { label: "21–60 sec.", test: (s: number) => s > 20 && s <= 60 },
    { label: "> 60 sec.", test: (s: number) => s > 60 },
  ]
    .map((b) => ({ label: b.label, value: stats.rows.filter((r) => b.test(r.depositSeconds)).length }))
    .filter((b) => b.value > 0);

  return (
    <Infographic
      id="infographic-verdeling"
      kicker={`Onderzoek · ${methodName}`}
      title={`Verdeling van onze ${stats.tests} metingen met ${methodName}`}
      description={`Elke staaf telt het aantal transacties dat binnen dat tijdvak werd bijgeschreven. Gemiddeld zetten we ${Math.round(stats.totalStaked / stats.tests)} euro per test in bij ${stats.casinos} verschillende aanbieders.`}
      source={ROUND}
    >
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr] lg:items-center">
        <ColumnChart
          items={buckets}
          caption={`Verdeling van gemeten stortingstijden met ${methodName}`}
          axisLabel="Tijd tot zichtbaar saldo"
          unitLabel="Aantal transacties"
        />
        <div className="space-y-5">
          <DonutChart
            percentage={stats.successRate}
            label="Zonder handmatige tussenkomst geslaagd"
            sublabel={`${stats.tests} transacties met ${methodName}`}
          />
          <DonutChart
            percentage={stats.kycRate}
            label="Identificatie gevraagd tijdens de test"
            sublabel="Aandeel tests waarbij het casino om documenten vroeg"
          />
        </div>
      </div>
    </Infographic>
  );
}

/** Geldstroom-diagram op basis van de feiten van de methode. */
export function MethodFlowInfographic({ methodSlug, methodName }: { methodSlug: string; methodName: string }) {
  const facts = getMethodFacts(methodSlug);
  if (!facts) return null;

  const steps = [
    { title: "1. Betaalopdracht", text: `Je kiest ${methodName} in de kassa van het casino en bevestigt het bedrag.` },
    { title: "2. Authenticatie", text: facts.strongAuth },
    { title: "3. Verwerking", text: facts.flow },
    { title: "4. Bijschrijving", text: facts.settlement },
  ];

  return (
    <Infographic
      id="infographic-geldstroom"
      kicker={`Onderzoek · ${methodName}`}
      title={`Zo loopt je geld bij ${methodName}`}
      description={`${facts.operator} verwerkt de transactie. Terugboeken werkt anders dan bij een creditcard: ${facts.chargeback.toLowerCase()}.`}
      source={facts.sources.map((s) => s.label).join(", ")}
    >
      <FlowDiagram steps={steps} />
      <dl className="mt-5 grid gap-3 text-sm sm:grid-cols-3">
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Kosten voor de speler</dt>
          <dd className="mt-1 font-medium text-foreground">{facts.playerCost}</dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Kosten voor het casino</dt>
          <dd className="mt-1 font-medium text-foreground">{facts.merchantCost}</dd>
        </div>
        <div className="rounded-lg border border-border p-3">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">Valuta</dt>
          <dd className="mt-1 font-medium text-foreground">{facts.currencies}</dd>
        </div>
      </dl>
    </Infographic>
  );
}

/** Wie welke tests uitvoerde: transparantie over de redactionele meetreeks. */
export function TesterCoverageInfographic() {
  const byTester = new Map<string, number>();
  for (const t of PAYMENT_TESTS) byTester.set(t.tester, (byTester.get(t.tester) ?? 0) + 1);
  const items = [...byTester.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([tester, count]) => ({
      label: tester,
      value: count,
      valueLabel: `${count} transacties`,
      hint: `${new Set(PAYMENT_TESTS.filter((t) => t.tester === tester).map((t) => t.method)).size} betaalmethoden · ${new Set(PAYMENT_TESTS.filter((t) => t.tester === tester).map((t) => t.casino)).size} aanbieders`,
    }));

  return (
    <Infographic
      id="infographic-testers"
      kicker="Onderzoek · verantwoording"
      title="Wie voerde welke betaaltests uit?"
      description={`Elke transactie in onze meetreeks is herleidbaar tot een redacteur met naam en toenaam. Samen voerden zij ${TEST_SUMMARY.testsRun} transacties uit in de ronde van ${TEST_SUMMARY.lastRound}.`}
      source={ROUND}
    >
      <BarChart items={items} caption="Aantal uitgevoerde betaaltests per redacteur" unitLabel="Aantal transacties" />
    </Infographic>
  );
}
